/**
 * OCPP configuration table. Two grouping modes:
 *   - `section` (default, OCPP 1.6): Core / Auth / SmartCharging / Custom.
 *   - `component` (OCPP 2.0.1, Session 19b): groups by the Device Model
 *     Component — the part of the "Component.Variable" key before the
 *     first dot — so the v2.0.1 inventory reads as a Component tree.
 * Read-only keys are shown but not editable; Custom keys get a Delete
 * action. Reset-to-default per row falls back to the version's defaults.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ConfigurationKeySnapshot } from '@/state/types';
import { ConfigurationKeyType, ConfigurationSection } from '@/state/types';
import { Toggle } from './Toggle';

interface Props {
  readonly entries: readonly ConfigurationKeySnapshot[];
  readonly onSetValue: (key: string, value: string) => void;
  readonly onResetToDefault: (key: string) => void;
  readonly onDelete: (key: string) => void;
  /** Grouping strategy. Defaults to `section` (v1.6). */
  readonly groupBy?: 'section' | 'component';
}

const SECTION_ORDER = [
  ConfigurationSection.Core,
  ConfigurationSection.Auth,
  ConfigurationSection.SmartCharging,
  ConfigurationSection.Custom,
] as const;

const SECTION_LABEL: Record<string, string> = {
  [ConfigurationSection.Core]: 'Core',
  [ConfigurationSection.Auth]: 'Auth',
  [ConfigurationSection.SmartCharging]: 'SmartCharging',
  [ConfigurationSection.Custom]: 'Custom',
};

export function ConfigurationTable({
  entries,
  onSetValue,
  onResetToDefault,
  onDelete,
  groupBy = 'section',
}: Props): JSX.Element {
  // Ordered list of [groupId, label, rows]. Section mode uses the fixed
  // SECTION_ORDER; component mode derives groups from the key prefix and
  // orders them alphabetically.
  const groups = useMemo(() => {
    const m = new Map<string, ConfigurationKeySnapshot[]>();
    for (const e of entries) {
      const id = groupBy === 'component' ? componentOf(e.key) : e.section;
      const list = m.get(id) ?? [];
      list.push(e);
      m.set(id, list);
    }
    for (const list of m.values()) list.sort((a, b) => a.key.localeCompare(b.key));
    const order =
      groupBy === 'component'
        ? [...m.keys()].sort((a, b) => a.localeCompare(b))
        : (SECTION_ORDER as readonly string[]);
    return order
      .map((id) => ({ id, label: SECTION_LABEL[id] ?? id, rows: m.get(id) ?? [] }))
      .filter((g) => g.rows.length > 0);
  }, [entries, groupBy]);

  return (
    <div className="space-y-3" data-testid="config-table">
      {groups.map((group) => (
        <section
          key={group.id}
          className="rounded border border-slate-800 bg-slate-900/50"
          data-testid={`config-section-${group.id}`}
        >
          <header className="border-b border-slate-800 px-3 py-1 text-[10px] uppercase tracking-wide text-slate-500">
            {group.label}
          </header>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-left text-xs">
            <tbody>
              {group.rows.map((e) => (
                <Row
                  key={e.key}
                  entry={e}
                  onSetValue={onSetValue}
                  onResetToDefault={onResetToDefault}
                  onDelete={onDelete}
                />
              ))}
            </tbody>
          </table>
          </div>
        </section>
      ))}
    </div>
  );
}

/** Component half of a "Component.Variable" key; the whole key if no dot. */
function componentOf(key: string): string {
  const dot = key.indexOf('.');
  return dot > 0 ? key.slice(0, dot) : key;
}

function Row({
  entry,
  onSetValue,
  onResetToDefault,
  onDelete,
}: {
  entry: ConfigurationKeySnapshot;
  onSetValue: (key: string, value: string) => void;
  onResetToDefault: (key: string) => void;
  onDelete: (key: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string>(entry.value);
  // Re-sync local draft when entry.value changes externally (e.g. Reset).
  useEffect(() => {
    setDraft(entry.value);
  }, [entry.value]);
  const isCustom = entry.section === ConfigurationSection.Custom;
  const isBoolean = entry.type === ConfigurationKeyType.Boolean;
  const dirty = draft !== entry.value;
  // Session 18 — booleans render as a toggle switch that flips on click
  // and calls onSetValue immediately. Typing 'true'/'false' into a text
  // field was a papercut (case-sensitive, fat-finger prone). Wire format
  // stays the string literal 'true'/'false' so GetConfiguration /
  // GetVariables replies are unchanged.
  const isOn = entry.value === 'true';
  return (
    <tr className="border-t border-slate-800" data-testid={`config-row-${entry.key}`}>
      <td className="px-2 py-1 align-middle font-mono text-slate-200">{entry.key}</td>
      {/* VC-C — fixed-width value column so every value type (read-only text,
          toggle, editable input) starts at the same x next to the key. */}
      <td className="w-52 px-2 py-1 text-left align-middle">
        {entry.readonly ? (
          <span
            className="block max-w-full truncate font-mono text-slate-500"
            title={entry.value}
            data-testid={`config-value-${entry.key}`}
          >
            {entry.value}
          </span>
        ) : isBoolean ? (
          <Toggle
            checked={isOn}
            onChange={(next) => onSetValue(entry.key, next ? 'true' : 'false')}
            label={`${entry.key} — ${isOn ? 'enabled' : 'disabled'}`}
            testId={`config-toggle-${entry.key}`}
          />
        ) : (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full rounded border border-slate-600 bg-slate-950 px-2 py-0.5 font-mono text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
            data-testid={`config-input-${entry.key}`}
          />
        )}
      </td>
      <td className="px-2 py-1 align-middle text-[10px] text-slate-500">{entry.type}</td>
      <td className="px-2 py-1 align-middle text-right">
        {!entry.readonly && !isBoolean ? (
          <button
            type="button"
            disabled={!dirty}
            onClick={() => onSetValue(entry.key, draft)}
            className="mr-1 rounded bg-primary-hover px-2 py-0.5 text-[10px] text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-30"
            data-testid={`config-save-${entry.key}`}
          >
            Save
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            onResetToDefault(entry.key);
            setDraft(entry.value); // optimistic local clear; refresh will overwrite
          }}
          disabled={isCustom}
          className="mr-1 rounded bg-slate-700 px-2 py-0.5 text-[10px] text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-30"
          title={isCustom ? 'Custom keys have no default' : 'Reset to default'}
          data-testid={`config-reset-${entry.key}`}
        >
          Reset
        </button>
        {isCustom ? (
          <button
            type="button"
            onClick={() => onDelete(entry.key)}
            className="rounded bg-rose-700 px-2 py-0.5 text-[10px] text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300"
            title="Custom key — delete it (standard keys can only be reset)"
            data-testid={`config-delete-${entry.key}`}
          >
            Delete
          </button>
        ) : null}
      </td>
    </tr>
  );
}
